/**
 * TaskHistorySection — Collapsible timeline of status changes for a task.
 * Lazy-fetches when expanded to avoid unnecessary API calls.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { History, ChevronDown, User, Clock, ArrowRight } from 'lucide-react';
import { tasksAPI } from '../lib/api';

const STATUS_LABELS = {
  'estados.en_ejecucion': 'En Progreso',
  'estados.pendiente': 'Pendiente',
  'estados.pendiente_entidad_corto': 'Pend. Entidad',
  'estados.rechazada_corto': 'Bloqueada',
  'estados.reparacion.finalizada': 'Completada',
  'estados.asignada_reparador': 'Asignada',
  'estados.en_reparacion': 'En Reparación',
  'estados.completada': 'Completada',
  'estados.rechazada': 'Rechazada',
  'estados.pausada': 'Pausada',
  'estados.bloqueada': 'Bloqueada',
};

const STATUS_COLORS = {
  'estados.reparacion.finalizada': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  'estados.completada': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  'estados.en_ejecucion': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  'estados.en_reparacion': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  'estados.rechazada': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  'estados.rechazada_corto': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  'estados.bloqueada': 'bg-red-100 text-red-700',
  'estados.pendiente': 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  'estados.pendiente_entidad_corto': 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  'default': 'bg-muted text-muted-foreground',
};

function StatusPill({ status }) {
  const label = STATUS_LABELS[status] || status || 'Sin estado';
  const color = STATUS_COLORS[status] || STATUS_COLORS.default;
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${color}`}>
      {label}
    </span>
  );
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function TaskHistorySection({ taskId }) {
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['task-history', taskId],
    queryFn: () => tasksAPI.getTaskHistory(taskId).then(r => r.data),
    enabled: expanded && !!taskId,
    staleTime: 60 * 1000,
  });

  const history = data?.history || [];

  return (
    <div className="border-t border-border">
      <button
        onClick={() => setExpanded(e => !e)}
        data-testid="task-history-toggle"
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">Historial de Estados</span>
          {data && history.length > 0 && (
            <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full">
              {history.length}
            </span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="px-4 pb-4">
          {isLoading ? (
            <div className="flex items-center gap-2 py-4 text-xs text-muted-foreground">
              <Clock className="w-3.5 h-3.5 animate-pulse" /> Cargando historial...
            </div>
          ) : history.length === 0 ? (
            <div className="py-4 text-center">
              <History className="w-6 h-6 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">
                Sin historial registrado aún.
              </p>
              <p className="text-[11px] text-muted-foreground/60 mt-1">
                Los cambios de estado se registran a partir de ahora.
              </p>
            </div>
          ) : (
            <div className="relative">
              {/* Vertical timeline line */}
              <div className="absolute left-[11px] top-4 bottom-4 w-0.5 bg-border" />
              <div className="space-y-3">
                {history.map((entry, i) => (
                  <div key={i} className="relative flex gap-3" data-testid={`history-entry-${i}`}>
                    {/* Timeline dot */}
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 border-background flex items-center justify-center z-10 ${i === 0 ? 'bg-primary' : 'bg-muted'}`}>
                      <div className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-white' : 'bg-muted-foreground/40'}`} />
                    </div>
                    {/* Entry card */}
                    <div className="flex-1 min-w-0 bg-muted/30 rounded-lg p-3 border border-border/50">
                      {/* Status transition */}
                      <div className="flex items-center gap-1.5 flex-wrap mb-2">
                        {entry.from_status ? (
                          <>
                            <StatusPill status={entry.from_status} />
                            <ArrowRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                          </>
                        ) : null}
                        <StatusPill status={entry.to_status} />
                      </div>
                      {/* Metadata row */}
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                          <User className="w-3 h-3" />
                          <span className="truncate max-w-[120px]">
                            {entry.changed_by || entry.changed_by_email || 'Sistema'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {formatDateTime(entry.changed_at)}
                        </div>
                      </div>
                      {/* Comment */}
                      {entry.comentario && (
                        <p className="text-[11px] text-foreground/70 mt-1.5 italic border-l-2 border-border pl-2">
                          &ldquo;{entry.comentario}&rdquo;
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
